import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    LineController,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format, subMonths, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import './DashboardFinanceiro.css';

// Registrar componentes do Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    LineController,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const DashboardFinanceiro = () => {
    const [mesSelecionado, setMesSelecionado] = useState(format(new Date(), 'yyyy-MM'));
    const [resumo, setResumo] = useState(null);
    const [fluxoSemanal, setFluxoSemanal] = useState([]);
    const [statusSync, setStatusSync] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sincronizando, setSincronizando] = useState(false);
    const [detalhesAbertos, setDetalhesAbertos] = useState(null);
    const [detalhes, setDetalhes] = useState(null);
    const [analiseVencimentos, setAnaliseVencimentos] = useState(null);
    const API_URL = '';

    // Buscar dados ao carregar e quando mudar o mês
    useEffect(() => {
        buscarDados();
        buscarStatusSync();
        buscarAnaliseVencimentos();
        
        // Atualizar status a cada 30 segundos
        const interval = setInterval(buscarStatusSync, 30000);
        return () => clearInterval(interval);
    }, [mesSelecionado]);

    const buscarDados = async () => {
        setLoading(true);
        try {
            const [resumoRes, fluxoRes] = await Promise.all([
                fetch(`${API_URL}/api/financeiro/resumo/${mesSelecionado}`),
                fetch(`${API_URL}/api/financeiro/fluxo-semanal/${mesSelecionado}`)
            ]);

            const resumoData = await resumoRes.json();
            const fluxoData = await fluxoRes.json();

            if (resumoData.success) setResumo(resumoData.resumo);
            if (fluxoData.success) setFluxoSemanal(fluxoData.semanas);
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        } finally {
            setLoading(false);
        }
    };

    const buscarStatusSync = async () => {
        try {
            const res = await fetch(`${API_URL}/api/financeiro/sync/status`);
            const data = await res.json();
            if (data.success) setStatusSync(data.status);
        } catch (error) {
            console.error('Erro ao buscar status:', error);
        }
    };

    const buscarAnaliseVencimentos = async () => {
        try {
            const res = await fetch(`${API_URL}/api/financeiro/analise-vencimentos/${mesSelecionado}`);
            const data = await res.json();
            if (data.success) {
                setAnaliseVencimentos(data.analise);
            }
        } catch (error) {
            console.error('Erro ao buscar análise de vencimentos:', error);
        }
    };

    const sincronizar = async () => {
        setSincronizando(true);
        try {
            const res = await fetch(`${API_URL}/api/financeiro/sync/sincronizar-aguardar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mes: mesSelecionado })
            });

            const data = await res.json();
            
            if (data.success) {
                alert('✅ Sincronização concluída!\n\n' +
                      `💰 Pagamentos: ${data.dados.pagamentos}\n` +
                      `📥 Recebimentos: ${data.dados.recebimentos}\n` +
                      `⏱️ Tempo: ${data.dados.duracao}s`);
                buscarDados();
                buscarStatusSync();
                buscarAnaliseVencimentos();
            } else {
                alert('❌ Erro na sincronização: ' + data.message);
            }
        } catch (error) {
            console.error('Erro ao sincronizar:', error);
            alert('❌ Erro ao sincronizar. Verifique o console.');
        } finally {
            setSincronizando(false);
        }
    };

    const buscarDetalhesSemana = async (semana) => {
        try {
            const res = await fetch(`${API_URL}/api/financeiro/semana-detalhes/${mesSelecionado}/${semana}`);
            const data = await res.json();
            
            if (data.success) {
                setDetalhes(data);
                setDetalhesAbertos(semana);
            }
        } catch (error) {
            console.error('Erro ao buscar detalhes:', error);
        }
    };

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    };

    const formatarData = (mesAno) => {
        const [ano, mes] = mesAno.split('-');
        const data = new Date(ano, mes - 1);
        return format(data, 'MMMM/yyyy', { locale: ptBR });
    };

    const mudarMes = (direcao) => {
        const [ano, mes] = mesSelecionado.split('-');
        const data = new Date(ano, mes - 1);
        const novaData = direcao === 'anterior' ? subMonths(data, 1) : addMonths(data, 1);
        setMesSelecionado(format(novaData, 'yyyy-MM'));
        setDetalhesAbertos(null);
    };

    // Dados do gráfico
    const dadosGrafico = fluxoSemanal.length > 0 ? {
        labels: fluxoSemanal.map(s => `Semana ${s.semana}`),
        datasets: [
            {
                label: 'A Receber',
                data: fluxoSemanal.map(s => s.total_receber),
                backgroundColor: 'rgba(34, 197, 94, 0.5)',
                borderColor: 'rgb(34, 197, 94)',
                borderWidth: 2
            },
            {
                label: 'A Pagar',
                data: fluxoSemanal.map(s => s.total_pagar),
                backgroundColor: 'rgba(239, 68, 68, 0.5)',
                borderColor: 'rgb(239, 68, 68)',
                borderWidth: 2
            },
            {
                type: 'line',
                label: 'Saldo',
                data: fluxoSemanal.map(s => s.saldo),
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 3,
                fill: true,
                tension: 0.3
            }
        ]
    } : null;

    const opcoesGrafico = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Fluxo de Caixa Semanal',
                font: { size: 16 }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.dataset.label || '';
                        const valor = formatarMoeda(context.parsed.y);
                        return `${label}: ${valor}`;
                    }
                }
            }
        },
        scales: {
            y: {
                ticks: {
                    callback: (value) => formatarMoeda(value)
                }
            }
        }
    };

    if (loading && !resumo) {
        return (
            <div className="dashboard-financeiro loading">
                <div className="spinner"></div>
                <p>Carregando dados financeiros...</p>
            </div>
        );
    }

    return (
        <div className="dashboard-financeiro">
            {/* HEADER */}
            <div className="dashboard-header">
                <div className="header-left">
                    <h1>💰 Dashboard Financeiro</h1>
                    <p className="mes-selecionado">
                        {formatarData(mesSelecionado).charAt(0).toUpperCase() + 
                         formatarData(mesSelecionado).slice(1)}
                    </p>
                </div>

                <div className="header-right">
                    {/* Navegação de mês */}
                    <div className="mes-navegacao">
                        <button onClick={() => mudarMes('anterior')} className="btn-mes">
                            ← Anterior
                        </button>
                        <button onClick={() => mudarMes('proximo')} className="btn-mes">
                            Próximo →
                        </button>
                    </div>

                    {/* Botão sincronizar */}
                    <button 
                        onClick={sincronizar} 
                        className="btn-sincronizar"
                        disabled={sincronizando}
                    >
                        {sincronizando ? '⏳ Sincronizando...' : '🔄 Sincronizar'}
                    </button>

                    {/* Status */}
                    {statusSync && (
                        <div className="sync-status">
                            <span className="sync-indicator">●</span>
                            Última sync: {statusSync.ultima_sincronizacao 
                                ? new Date(statusSync.ultima_sincronizacao).toLocaleTimeString('pt-BR')
                                : 'Nunca'}
                        </div>
                    )}
                </div>
            </div>

            {/* CARDS DE RESUMO */}
            {resumo && (
                <div className="cards-resumo">
                    <div className="card card-receber">
                        <div className="card-header">
                            <h3>📥 A Receber</h3>
                            <span className="badge badge-receber">
                                {resumo.taxa_recebimento}% recebido
                            </span>
                        </div>
                        <div className="card-valor">{formatarMoeda(resumo.total_receber)}</div>
                        <div className="card-detalhes">
                            <div className="detalhe">
                                <span>Recebido:</span>
                                <strong className="text-success">{formatarMoeda(resumo.recebido)}</strong>
                            </div>
                            <div className="detalhe">
                                <span>Pendente:</span>
                                <strong className="text-warning">{formatarMoeda(resumo.pendente_receber)}</strong>
                            </div>
                        </div>
                    </div>

                    <div className="card card-pagar">
                        <div className="card-header">
                            <h3>📤 A Pagar</h3>
                            <span className="badge badge-pagar">
                                {resumo.taxa_pagamento}% pago
                            </span>
                        </div>
                        <div className="card-valor">{formatarMoeda(resumo.total_pagar)}</div>
                        <div className="card-detalhes">
                            <div className="detalhe">
                                <span>Pago:</span>
                                <strong className="text-success">{formatarMoeda(resumo.pago)}</strong>
                            </div>
                            <div className="detalhe">
                                <span>Pendente:</span>
                                <strong className="text-danger">{formatarMoeda(resumo.pendente_pagar)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* CARD FLUXO DE CAIXA SIMPLES */}
                    <div className={`card card-saldo ${resumo.saldo_final >= 0 ? 'positivo' : 'negativo'}`}>
                        <div className="card-header">
                            <h3>💰 Fluxo de Caixa</h3>
                            <span className={`badge ${resumo.saldo_final >= 0 ? 'badge-positivo' : 'badge-negativo'}`}>
                                {resumo.saldo_final >= 0 ? 'Positivo' : 'Déficit'}
                            </span>
                        </div>
                        
                        <div className="fluxo-linha-principal">
                            <div className="fluxo-valor-box">
                                <span className="fluxo-label">Início</span>
                                <div className="fluxo-valor">{formatarMoeda(resumo.saldo_inicial)}</div>
                            </div>
                            <span className="fluxo-separador">→</span>
                            <div className="fluxo-valor-box destaque">
                                <span className="fluxo-label">Atual</span>
                                <div className={`fluxo-valor-destaque ${resumo.saldo_final >= 0 ? 'positivo' : 'negativo'}`}>
                                    {formatarMoeda(resumo.saldo_final)}
                                </div>
                            </div>
                            <span className="fluxo-separador">→</span>
                            <div className="fluxo-valor-box">
                                <span className="fluxo-label">Projeção</span>
                                <div className={`fluxo-valor ${resumo.saldo_projetado >= 0 ? 'positivo' : 'negativo'}`}>
                                    {formatarMoeda(resumo.saldo_projetado)}
                                </div>
                            </div>
                        </div>

                        <div className="card-detalhes">
                            <div className="detalhe">
                                <span>Recebido:</span>
                                <strong className="text-success">{formatarMoeda(resumo.recebido)}</strong>
                            </div>
                            <div className="detalhe">
                                <span>Pago:</span>
                                <strong className="text-danger">{formatarMoeda(resumo.pago)}</strong>
                            </div>
                            {resumo.emprestimo_recebido > 0 && (
                                <div className="detalhe detalhe-emprestimo">
                                    <span>Empréstimo:</span>
                                    <strong className="text-warning">{formatarMoeda(resumo.emprestimo_recebido)}</strong>
                                </div>
                            )}
                            <div className="detalhe">
                                <span>A Receber:</span>
                                <strong>{formatarMoeda(resumo.pendente_receber)}</strong>
                            </div>
                            <div className="detalhe">
                                <span>A Pagar:</span>
                                <strong>{formatarMoeda(resumo.pendente_pagar)}</strong>
                            </div>
                        </div>

                        {resumo.saldo_projetado < 0 && (
                            <div className="meta-equilibrio">
                                <strong>Meta:</strong> Gerar {formatarMoeda(Math.abs(resumo.saldo_projetado))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* CARD DE ANÁLISE DE VENCIMENTOS */}
            {analiseVencimentos && (
                <div className={`card-analise-vencimentos status-${analiseVencimentos.status}`}>
                    <div className="analise-header">
                        <h3>🎯 Análise de Vencimentos</h3>
                        <span className={`badge-status badge-${analiseVencimentos.recomendacao.cor}`}>
                            {analiseVencimentos.status.toUpperCase()}
                        </span>
                    </div>

                    <div className={`recomendacao-box recomendacao-${analiseVencimentos.recomendacao.cor}`}>
                        <h4>{analiseVencimentos.recomendacao.titulo}</h4>
                        <p>{analiseVencimentos.recomendacao.texto}</p>
                    </div>

                    <div className="analise-detalhes">
                        <div className="analise-item">
                            <span className="analise-label">📅 Melhor Período:</span>
                            <strong className="analise-valor">{analiseVencimentos.melhor_periodo}</strong>
                        </div>
                        <div className="analise-item">
                            <span className="analise-label">💰 Capacidade Total:</span>
                            <strong className="analise-valor">{formatarMoeda(analiseVencimentos.capacidade_total)}</strong>
                        </div>
                        <div className="analise-item">
                            <span className="analise-label">🎯 Melhor Semana:</span>
                            <strong className="analise-valor">{formatarMoeda(analiseVencimentos.capacidade_melhor_semana)}</strong>
                        </div>
                    </div>

                    {/* Semáforo Visual das Semanas */}
                    <div className="semanas-visual">
                        <h5>📊 Capacidade por Semana:</h5>
                        <div className="semanas-grid">
                            {analiseVencimentos.semanas.map(s => (
                                <div key={s.semana} className={`semana-box status-${s.status}`}>
                                    <div className="semana-numero">Sem {s.semana}</div>
                                    <div className="semana-capacidade">{formatarMoeda(s.capacidade_pagamento)}</div>
                                    <div className="semana-indicador">
                                        {s.status === 'otimo' && '🟢'}
                                        {s.status === 'bom' && '🟢'}
                                        {s.status === 'moderado' && '🟡'}
                                        {s.status === 'ruim' && '🔴'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* GRÁFICO */}
            {dadosGrafico && (
                <div className="grafico-container">
                    <div className="grafico-wrapper">
                        <Bar data={dadosGrafico} options={opcoesGrafico} />
                    </div>
                </div>
            )}

            {/* TABELA FLUXO SEMANAL */}
            {fluxoSemanal.length > 0 && (
                <div className="tabela-container">
                    <h2>📊 Fluxo Semanal Detalhado</h2>
                    <table className="tabela-fluxo">
                        <thead>
                            <tr>
                                <th>Semana</th>
                                <th>A Receber</th>
                                <th>Recebido</th>
                                <th>A Pagar</th>
                                <th>Pago</th>
                                <th>Saldo</th>
                                <th>Saldo Acum.</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fluxoSemanal.map((semana) => (
                                <React.Fragment key={semana.semana}>
                                    <tr 
                                        className={`linha-semana ${detalhesAbertos === semana.semana ? 'expandida' : ''}`}
                                        onClick={() => detalhesAbertos === semana.semana 
                                            ? setDetalhesAbertos(null) 
                                            : buscarDetalhesSemana(semana.semana)}
                                    >
                                        <td><strong>Semana {semana.semana}</strong></td>
                                        <td className="text-receber">{formatarMoeda(semana.total_receber)}</td>
                                        <td className="text-success">{formatarMoeda(semana.recebido)}</td>
                                        <td className="text-pagar">{formatarMoeda(semana.total_pagar)}</td>
                                        <td className="text-success">{formatarMoeda(semana.pago)}</td>
                                        <td className={semana.saldo >= 0 ? 'text-success' : 'text-danger'}>
                                            <strong>{formatarMoeda(semana.saldo)}</strong>
                                        </td>
                                        <td className={semana.saldo_acumulado >= 0 ? 'text-success' : 'text-danger'}>
                                            <strong>{formatarMoeda(semana.saldo_acumulado)}</strong>
                                        </td>
                                        <td>
                                            <button className="btn-detalhes">
                                                {detalhesAbertos === semana.semana ? '▼ Fechar' : '▶ Detalhes'}
                                            </button>
                                        </td>
                                    </tr>

                                    {/* DETALHES DA SEMANA */}
                                    {detalhesAbertos === semana.semana && detalhes && (
                                        <tr className="linha-detalhes">
                                            <td colSpan="8">
                                                <div className="detalhes-semana">
                                                    <div className="detalhes-coluna">
                                                        <h4>📥 Contas a Receber ({detalhes.receber.quantidade})</h4>
                                                        {detalhes.receber.itens.length > 0 ? (
                                                            <ul className="lista-contas">
                                                                {detalhes.receber.itens.slice(0, 5).map((item) => (
                                                                    <li key={item.id}>
                                                                        <span className="conta-desc">{item.descricao}</span>
                                                                        <span className="conta-cliente">{item.cliente_nome}</span>
                                                                        <span className="conta-valor">{formatarMoeda(item.valor_total)}</span>
                                                                        <span className={`conta-status status-${item.status}`}>
                                                                            {item.status}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p className="sem-dados">Nenhuma conta a receber</p>
                                                        )}
                                                    </div>

                                                    <div className="detalhes-coluna">
                                                        <h4>📤 Contas a Pagar ({detalhes.pagar.quantidade})</h4>
                                                        {detalhes.pagar.itens.length > 0 ? (
                                                            <ul className="lista-contas">
                                                                {detalhes.pagar.itens.slice(0, 5).map((item) => (
                                                                    <li key={item.id}>
                                                                        <span className="conta-desc">{item.descricao}</span>
                                                                        <span className="conta-fornecedor">{item.fornecedor}</span>
                                                                        <span className="conta-valor">{formatarMoeda(item.valor_total)}</span>
                                                                        <span className={`conta-status status-${item.status}`}>
                                                                            {item.status}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p className="sem-dados">Nenhuma conta a pagar</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DashboardFinanceiro;